import { useEffect, useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { OutputWithUrl } from "../lib/types.js";

// Renders one generated variant by mime type. SVG and raster images are shown
// only through <img src> (INV-5: never dangerouslySetInnerHTML), so a
// malicious SVG that slipped past the sanitizer still can't execute here.

const btnGhost =
  "rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 " +
  "dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800";

function isTextual(mimeType: string): boolean {
  return (
    mimeType.startsWith("text/") ||
    mimeType === "application/json" ||
    mimeType === "application/xml"
  );
}

function DownloadLink({ url }: { url: string }) {
  return (
    <a href={url} target="_blank" rel="noreferrer" className={btnGhost}>
      Download
    </a>
  );
}

function TextPreview({ output }: { output: OutputWithUrl }) {
  const [text, setText] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setText(null);
    setFailed(false);
    fetch(output.url, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        return res.text();
      })
      .then((body) => setText(body))
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setText(output.textPreview ?? "");
        setFailed(true);
      });
    return () => controller.abort();
  }, [output.url, output.textPreview]);

  if (text === null) {
    return <p className="text-xs text-gray-400">Loading preview...</p>;
  }

  const isJson = output.mimeType === "application/json";
  let body = text;
  if (isJson) {
    try {
      body = JSON.stringify(JSON.parse(text), null, 2);
    } catch {
      /* leave as-is if it isn't valid JSON after all */
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(text ?? "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked - no-op */
    }
  }

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <button type="button" className={btnGhost} onClick={() => void copy()}>
          {copied ? "Copied" : "Copy"}
        </button>
        <DownloadLink url={output.url} />
        {failed && <span className="text-xs text-amber-600">showing cached preview</span>}
      </div>
      {isJson ? (
        <pre className="max-h-96 overflow-auto rounded-md bg-gray-50 p-3 text-xs text-gray-800 dark:bg-gray-900 dark:text-gray-200">
          {body}
        </pre>
      ) : (
        <div className="prose prose-sm max-h-96 max-w-none overflow-auto dark:prose-invert">
          <Markdown remarkPlugins={[remarkGfm]}>{body}</Markdown>
        </div>
      )}
    </div>
  );
}

export function OutputPreview({ output }: { output: OutputWithUrl }) {
  const { mimeType, url } = output;

  if (mimeType === "image/svg+xml" || mimeType.startsWith("image/")) {
    return (
      <div>
        <img
          src={url}
          alt="Generated output"
          className="max-h-96 w-full rounded-md border border-gray-200 bg-[repeating-conic-gradient(#e5e7eb_0_25%,transparent_0_50%)] bg-[length:16px_16px] object-contain dark:border-gray-800"
        />
        <div className="mt-2">
          <DownloadLink url={url} />
        </div>
      </div>
    );
  }

  if (mimeType.startsWith("audio/")) {
    return (
      <div>
        <audio controls src={url} className="w-full" />
        <div className="mt-2">
          <DownloadLink url={url} />
        </div>
      </div>
    );
  }

  if (mimeType.startsWith("video/")) {
    return (
      <div>
        <video controls loop muted src={url} className="max-h-96 w-full rounded-md" />
        <div className="mt-2">
          <DownloadLink url={url} />
        </div>
      </div>
    );
  }

  if (isTextual(mimeType)) {
    return <TextPreview output={output} />;
  }

  return (
    <div className="text-xs text-gray-500 dark:text-gray-400">
      <p className="mb-2">No inline preview for {mimeType}.</p>
      <DownloadLink url={url} />
    </div>
  );
}
